<script setup lang="ts">
import { useI18n } from '@/i18n';

const { t } = useI18n();
</script>

<template>
  <section class="lp-hero" :aria-label="t('landing_hero_title')">
    <div class="lp-blob lp-blob--1" aria-hidden="true" />
    <div class="lp-blob lp-blob--2" aria-hidden="true" />
    <div class="lp-blob lp-blob--3" aria-hidden="true" />

    <div class="lp-container lp-hero-inner">
      <!-- Text -->
      <div class="lp-hero-text">
        <div class="lp-hero-badge">
          <span class="lp-badge-dot" aria-hidden="true" />
          {{ t('landing_hero_badge') }}
        </div>
        <h1 class="lp-hero-title">
          {{ t('landing_hero_title') }}<br />
          <span class="lp-hero-title-accent">{{ t('landing_hero_title_accent') }}</span>
        </h1>
        <p class="lp-hero-desc">{{ t('landing_hero_desc') }}</p>
      </div>

   
    </div>

    <div class="lp-scroll-hint" aria-hidden="true">
      <div class="lp-scroll-mouse"><div class="lp-scroll-dot" /></div>
    </div>
  </section>
</template>

<style scoped>
.lp-hero {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  overflow: hidden;
  padding-block: 120px 80px;
}
.lp-container { max-width: 1160px; margin-inline: auto; padding-inline: 24px; }

.lp-blob {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  filter: blur(90px);
  animation: lp-blob-drift 14s ease-in-out infinite alternate;
}
.lp-blob--1 { width: 600px; height: 600px; background: radial-gradient(circle, rgba(124,140,255,.22), transparent 70%); top: -200px; left: -200px; }
.lp-blob--2 { width: 400px; height: 400px; background: radial-gradient(circle, rgba(100,213,255,.16), transparent 70%); top: 0; right: -100px; animation-delay: -5s; animation-duration: 18s; }
.lp-blob--3 { width: 300px; height: 300px; background: radial-gradient(circle, rgba(156,232,118,.1), transparent 70%); bottom: 0; left: 40%; animation-delay: -9s; animation-duration: 20s; }
@keyframes lp-blob-drift { from { transform: translate(0,0) scale(1); } to { transform: translate(30px,40px) scale(1.08); } }

.lp-hero-inner {
  display: grid;
  align-items: center;
  gap: 80px;
  position: relative;
  z-index: 1;
}

.lp-hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  background: rgba(70,211,159,.12);
  border: 1px solid rgba(70,211,159,.25);
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  color: var(--success);
  margin-bottom: 28px;
}
.lp-badge-dot {
  width: 8px; height: 8px;
  background: var(--success);
  border-radius: 50%;
  animation: lp-pulse-dot 2s ease-in-out infinite;
}
@keyframes lp-pulse-dot { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.6;transform:scale(.75)} }

.lp-hero-title {
  font-size: clamp(36px, 4.5vw, 58px);
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -.03em;
  margin-bottom: 20px;
  color: var(--text-main);
}
.lp-hero-title-accent {
  background: linear-gradient(135deg, var(--primary-indigo) 0%, var(--secondary-cyan) 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}
.lp-hero-desc { font-size: 17px; line-height: 1.7; color: var(--text-muted); max-width: 480px; margin-bottom: 36px; }
.lp-hero-ctas { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; margin-bottom: 20px; }
.lp-hero-footnote { font-size: 12px; color: var(--text-faint); }

.lp-btn-primary {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 14px 28px; background: var(--primary-indigo); color: #fff;
  font-size: 15px; font-weight: 700; border: none; border-radius: 14px; cursor: pointer;
  transition:
    background var(--md-duration-short4) var(--md-easing-standard),
    box-shadow var(--md-duration-short4) var(--md-easing-standard),
    transform var(--md-duration-short4) var(--md-easing-spring);
}
.lp-btn-primary:hover { background: var(--primary-indigo-strong); box-shadow: 0 8px 28px rgba(124,140,255,.42); transform: translateY(-2px); }
.lp-btn-primary:active { transform: translateY(0); }

.lp-btn-ghost {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 14px 24px; background: rgba(255,255,255,.05); border: 1px solid var(--border-color);
  color: var(--text-muted); font-size: 15px; font-weight: 600; border-radius: 14px;
  transition: background var(--md-duration-short4), color var(--md-duration-short4), border-color var(--md-duration-short4);
}
.lp-btn-ghost--disabled { opacity: .45; cursor: not-allowed; }

/* Phone device */
.lp-hero-visual { display: flex; justify-content: center; align-items: center; }
.lp-device {
  width: 280px;
  background: #0b0f1a;
  border-radius: 44px;
  border: 2px solid rgba(255,255,255,.1);
  box-shadow: 0 0 0 1px rgba(255,255,255,.04), 0 40px 80px rgba(0,0,0,.6), 0 0 60px rgba(124,140,255,.08);
  overflow: hidden;
  animation: lp-phone-float 6s ease-in-out infinite;
}
@keyframes lp-phone-float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-14px)} }

.lp-screen { background: #0b0f1a; }
.lp-status-bar { display: flex; justify-content: space-between; align-items: center; padding: 10px 14px 4px; font-size: 10px; font-weight: 600; color: var(--text-main); }
.lp-status-icons { display: flex; gap: 6px; align-items: center; }
.lp-pill {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 5px 9px;
  background: rgba(124,140,255,.1);
  border: 1px solid rgba(124,140,255,.32);
  border-radius: 999px;
  color: #9da8ff;
  font-size: 7.5px; font-weight: 700;
  white-space: nowrap;
}
.lp-pill--sm { padding: 3px 8px; font-size: 7px; }

/* ── Carosello viste (Scopri / Binder / Wishlist / Mappa) ── */
.lp-carousel { overflow: hidden; height: 312px; }
.lp-track {
  display: flex;
  width: 400%;
  height: 100%;
  animation: lp-carousel-cycle 18s ease-in-out infinite;
}
.lp-slide {
  width: 25%;
  flex-shrink: 0;
  height: 100%;
  padding: 6px 10px 4px;
  display: flex;
  flex-direction: column;
  gap: 9px;
  box-sizing: border-box;
}
.lp-slide-head { display: flex; align-items: center; justify-content: space-between; gap: 6px; }
.lp-slide-title { font-size: 11px; font-weight: 800; color: var(--text-main); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.lp-slide-meta { font-size: 8.5px; font-weight: 700; color: var(--text-faint); flex-shrink: 0; }
.lp-slide .lp-wishlist-badge { align-self: center; }
.lp-slide--shot > :not(.lp-app-shot-wrap) { display: none !important; }
.lp-app-shot-wrap {
  width: 100%;
  height: 100%;
  min-height: 0;
  border-radius: 18px;
  overflow: hidden;
  background: #0b0f1a;
  border: 1px solid rgba(255,255,255,.08);
  box-shadow: inset 0 0 0 1px rgba(255,255,255,.03);
}
.lp-app-shot {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Binder */
.lp-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 7px; }
.lp-grid-card { width: 100%; aspect-ratio: 5 / 7; object-fit: cover; border-radius: 6px; background: rgba(255,255,255,.05); box-shadow: 0 4px 12px rgba(0,0,0,.5), 0 0 0 1px rgba(255,255,255,.06); }

/* Wishlist */
.lp-list { display: flex; flex-direction: column; gap: 8px; }
.lp-list-row { display: flex; align-items: center; gap: 9px; padding: 7px 9px; border-radius: 12px; background: rgba(255,255,255,.04); border: 1px solid rgba(255,255,255,.06); }
.lp-list-thumb { width: 34px; height: 47px; object-fit: cover; border-radius: 5px; flex-shrink: 0; background: rgba(255,255,255,.06); }
.lp-list-info { flex: 1; min-width: 0; }
.lp-list-info strong { display: block; font-size: 11px; font-weight: 700; color: var(--text-main); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.lp-list-info span { display: block; font-size: 8.5px; color: var(--text-faint); margin-top: 1px; }
.lp-tag { flex-shrink: 0; padding: 4px 9px; border-radius: 999px; font-size: 7.5px; font-weight: 800; letter-spacing: .05em; color: #c4b5fd; background: rgba(139,92,246,.18); border: 1px solid rgba(139,92,246,.38); }

/* Mappa */
.lp-map {
  position: relative; flex: 1; border-radius: 14px; overflow: hidden;
  border: 1px solid rgba(255,255,255,.08);
  background:
    radial-gradient(circle at 30% 30%, rgba(124,140,255,.12), transparent 60%),
    repeating-linear-gradient(0deg, rgba(255,255,255,.045) 0 1px, transparent 1px 26px),
    repeating-linear-gradient(90deg, rgba(255,255,255,.045) 0 1px, transparent 1px 26px),
    #101827;
}
.lp-map-pin { position: absolute; transform: translate(-50%, -50%); }
.lp-map-pin--me {
  top: 50%; left: 50%; width: 26px; height: 26px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 800; color: #fff;
  background: linear-gradient(135deg, #46d39f, #2ea87c);
  box-shadow: 0 0 0 4px rgba(70,211,159,.22);
}
.lp-map-dot {
  width: 16px; height: 16px; border-radius: 50%;
  background: var(--primary-indigo);
  box-shadow: 0 0 10px rgba(124,140,255,.7), 0 0 0 4px rgba(124,140,255,.18);
  animation: lp-map-dot-pulse 2.4s ease-in-out infinite;
}
@keyframes lp-map-dot-pulse {
  0%, 100% { transform: translate(-50%, -50%) scale(1); }
  50% { transform: translate(-50%, -50%) scale(1.22); }
}
@keyframes lp-carousel-cycle {
  0%, 20%  { transform: translateX(0); }
  25%, 45% { transform: translateX(-25%); }
  50%, 70% { transform: translateX(-50%); }
  75%, 95% { transform: translateX(-75%); }
  100%     { transform: translateX(0); }
}

/* Indicatori slide */
.lp-dots { display: flex; justify-content: center; gap: 5px; padding: 6px 0 8px; background: #0b0f1a; }
.lp-dot { width: 5px; height: 5px; border-radius: 50%; background: rgba(255,255,255,.25); animation: lp-dot-pulse 18s infinite; }
.lp-dot:nth-child(2) { animation-delay: -13.5s; }
.lp-dot:nth-child(3) { animation-delay: -9s; }
.lp-dot:nth-child(4) { animation-delay: -4.5s; }
@keyframes lp-dot-pulse {
  0%, 20% { background: var(--primary-indigo); transform: scale(1.3); }
  21%, 100% { background: rgba(255,255,255,.25); transform: scale(1); }
}

.lp-card-stack { position: relative; width: 100%; height: 188px; }
.lp-tcg-bg { position: absolute; width: 105px; height: 147px; border-radius: 8px; border: 1px solid rgba(255,255,255,.07); top: 18px; }
.lp-tcg-bg--l { left: 6px; background: linear-gradient(145deg, #1c2240, #121828); transform: rotate(-12deg); transform-origin: bottom center; opacity: .6; }
.lp-tcg-bg--r { right: 6px; background: linear-gradient(145deg, #2a1e06, #1e1506); border-color: rgba(200,160,40,.14); transform: rotate(10deg); transform-origin: bottom center; opacity: .72; }
.lp-tcg-front { position: absolute; left: 50%; top: 6px; transform: translateX(-50%); width: 125px; z-index: 2; border-radius: 8px; overflow: hidden; box-shadow: 0 10px 30px rgba(0,0,0,.75), 0 0 0 1px rgba(255,255,255,.08); }
.lp-card-img { width: 100%; height: auto; display: block; }
.lp-wishlist-badge {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 5px 12px;
  background: rgba(139,92,246,.18);
  border: 1px solid rgba(139,92,246,.38);
  border-radius: 999px;
  color: #c4b5fd;
  font-size: 8.5px; font-weight: 700; letter-spacing: .07em;
}
.lp-user-row { display: flex; align-items: center; gap: 6px; padding: 9px 12px; background: rgba(255,255,255,.03); border-top: 1px solid rgba(255,255,255,.05); }
.lp-avatar { width: 30px; height: 30px; border-radius: 50%; background: linear-gradient(135deg, #46d39f, #2ea87c); display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 800; color: #fff; flex-shrink: 0; }
.lp-user-info { flex: 1; min-width: 0; }
.lp-username { font-size: 11px; font-weight: 700; color: var(--text-main); }
.lp-userdist { display: flex; align-items: center; gap: 2px; font-size: 9px; color: var(--text-faint); margin-top: 1px; }
.lp-action-btn { display: inline-flex; align-items: center; padding: 4px 8px; background: transparent; border: 1px solid rgba(255,255,255,.14); border-radius: 999px; color: var(--text-muted); font-size: 8px; font-weight: 600; flex-shrink: 0; }
.lp-bottom-nav { display: flex; align-items: flex-end; justify-content: space-around; padding: 8px 4px 2px; background: rgba(10,13,22,.96); border-top: 1px solid rgba(255,255,255,.06); }
.lp-nav-tab { display: flex; flex-direction: column; align-items: center; gap: 3px; color: rgba(255,255,255,.3); min-width: 44px; padding-bottom: 2px; }
.lp-nav-tab span { font-size: 7.5px; font-weight: 500; }
.lp-nav-tab--active { color: var(--primary-indigo); }
.lp-nav-pill { width: 42px; height: 26px; background: var(--primary-indigo); border-radius: 13px; display: flex; align-items: center; justify-content: center; margin-top: -6px; margin-bottom: 1px; box-shadow: 0 4px 14px rgba(124,140,255,.45); }
.lp-home-ind { height: 18px; display: flex; align-items: center; justify-content: center; background: #0b0f1a; }
.lp-home-ind::after { content: ''; width: 80px; height: 4px; background: rgba(255,255,255,.18); border-radius: 999px; }

.lp-scroll-hint { position: absolute; bottom: 32px; left: 50%; transform: translateX(-50%); z-index: 1; }
.lp-scroll-mouse { width: 22px; height: 36px; border: 2px solid rgba(255,255,255,.2); border-radius: 11px; display: flex; justify-content: center; padding-top: 6px; }
.lp-scroll-dot { width: 4px; height: 8px; background: var(--primary-indigo); border-radius: 2px; animation: lp-scroll-anim 2s ease-in-out infinite; }
@keyframes lp-scroll-anim { 0%{transform:translateY(0);opacity:1} 100%{transform:translateY(14px);opacity:0} }

@media (max-width: 900px) {
  .lp-hero-inner { grid-template-columns: 1fr; gap: 60px; text-align: center; }
  .lp-hero-text { order: 1; }
  .lp-hero-visual { order: 0; }
  .lp-hero-desc { margin-inline: auto; }
  .lp-hero-ctas { justify-content: center; }
  .lp-scroll-hint { display: none; }
}
@media (max-width: 480px) {
  .lp-hero { padding-block: 100px 60px; }
}
@media (prefers-reduced-motion: reduce) {
  .lp-track,
  .lp-dot,
  .lp-map-dot { animation: none !important; }
}
</style>
